import { useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import HandleChange from '../../../../Helpers/HandleChange';
import {
    MANAGE_CONTRACTORS_MODEL,
} from '../SubComponents/ManageContractors/Model/Model';
import {
    GetContractorRestriction, GetSupplierRestriction,
    UpdateContractorRestriction, UpdateSupplierRestriction
} from '../../../../reduxToolkit/restrictions/RestrictionApi';
import { useDispatch } from 'react-redux';
import { errorMsg, getDateLong } from '../../../../Apis/functions';
import { toast } from 'react-toastify';
import { FREQUENCY_RENEWAL } from '../../../../enums/FrequencyRenewalEnum';
import {PERIOD_TIME_UNIT, PERIOD_TIME_UNIT_ENUM} from '../../../../enums/PeriodTimeUnitEnum';
import { USER_TYPE_MODEL } from '../../../../enums/userTypeEnum';
import {
    FREQUENCY_VALUE_FIELD_NAME,
    getCurrentDay,
    RENEWAL_FIELD_NAME, START_DATE_FIELD_NAME,
    TIME_UNIT_FIELD_NAME
} from "../Model/Models";
import {calculateRange} from "../../../../components/CustomDateRangePicker/Model";
import {GetPeriodRange} from "../../../../reduxToolkit/DocumentationApproval/DocumentationApprovalApi";
import moment from "moment";

/**
 * @Author      Jhovanny Fragoso Marquez
 * @JobTitle    Frontend Developer
 * @Email       jfragoso@ibl.mx
 */


function ManageContractorsApp({
    ref,
    userType
}) {
    const { t } = useTranslation()
    const dispatch = useDispatch();
    const [config, setConfig] = useState({ ...MANAGE_CONTRACTORS_MODEL });
    const [emptyFields, setEmptyFields] = useState(false);
    const [modified, setModified] = useState(false);
    const { handleChange: onChange } = HandleChange({ model: config, setModel: setConfig });
    const [txtLadDays, setTxtLadDays] = useState("");
    const [txtFrequencyValue, setTxtFrequencyValue] = useState("");
    const [emptyFieldsCustom, setEmptyFieldsCustom] = useState(false);
    const [isValidFrequency, setIsValidFrequency] = useState(true);
    const [isValidLeadDays, setIsValidLeadDays] = useState(true);
    const [isValidLeadDaysByMonth, setIsValidLeadDaysByMonth] = useState(true);
    const [originalInfo, setOriginalInfo] = useState({ ...MANAGE_CONTRACTORS_MODEL });

    const TIME_UNIT_OPTIONS_TRANS = PERIOD_TIME_UNIT.map(el => ({
        ...el,
        name: t(el.name)
    }));

    /**
     * hanle to click update data
     */
    useImperativeHandle(ref, () => ({
        handleUpdate
    }));

    /**
     * handle to get data
     */
    useEffect(() => {
        getData();
    }, []);

    /**
     * handle to get data where modified has changes
     */
    useEffect(() => {
        if (modified) {
            getData();
            setModified(false);
        }
    }, [modified]);

    /**
     * Handle to validate when value equal zero,
     */
    useEffect(() => {
        const isCustomInterval = config?.renewal === FREQUENCY_RENEWAL.CUSTOM_INTERVAL;
        const frequencyValue = Number(config?.frequencyValue);
        const leadDays = Number(config?.leadDays);

        // -------- FREQUENCY --------
        if (isCustomInterval) {
            if (frequencyValue <= 0) {
                setIsValidFrequency(false);
                setTxtFrequencyValue(t("the_frequency_value_cannot_be_zero"));
            } else if (config?.timeUnit !== PERIOD_TIME_UNIT_ENUM.MONTHS.name && leadDays > 0 && !isValidFrequencyAndLeadDays()) {
                setIsValidFrequency(false);
                setTxtFrequencyValue(t("it_must_be_greater_than_the_allowed_lead_time"));
            } else {
                setIsValidFrequency(true);
                setTxtFrequencyValue("");
            }
        }else{
            setIsValidFrequency(true);
            setTxtFrequencyValue("");
        }

        // -------- LEAD DAYS --------
        if(isCustomInterval){
            if (config?.leadDays === "" ) {
                setIsValidLeadDays(true);
                setIsValidLeadDaysByMonth(true);
                setTxtLadDays("");
                setEmptyFields(true);
            }else if (leadDays === 0) {
                setIsValidLeadDays(false);
                setIsValidLeadDaysByMonth(true);
                setTxtLadDays(t("the_lead_days_cannot_be_zero"));
            } else if (isCustomInterval && !isValidFrequencyAndLeadDays()) {
                if(config?.timeUnit === PERIOD_TIME_UNIT_ENUM.MONTHS.name){
                    setTxtLadDays(t("must_be_31_or_less_maximum_days_in_a_month"));
                    setIsValidLeadDays(true);
                    setIsValidLeadDaysByMonth(false);
                }else{
                    setTxtLadDays(t("it_must_be_less_than_the_frequency"));
                    setIsValidLeadDays(false);
                    setIsValidLeadDaysByMonth(true);
                }
            } else {
                setIsValidLeadDays(true);
                setIsValidLeadDaysByMonth(true);
                setTxtLadDays("");
            }
        }
    }, [config?.renewal, config.frequencyValue, config.leadDays, config.timeUnit]);

    /**
     * Validates whether the configured lead days are valid according to the
     * frequency and time unit defined in the configuration.
     *
     * The calculation converts the configured frequency into total calendar days:
     * - DAYS: uses the frequency value directly as days.
     * - MONTHS: converts months into days using a fixed 31-day month base.
     * - YEARS: calculates the difference in days between the start date and the
     *   end date obtained by adding the frequency in years.
     *
     * The function ensures that leadDays does not exceed the total calculated
     * calendar days for the given period.
     *
     * @function isValidFrequencyAndLeadDays
     * @returns {boolean} true if leadDays is valid within the calculated period, otherwise false.
     */
    const isValidFrequencyAndLeadDays = () => {
        const frequencyValue = Number(config.frequencyValue);
        const leadDays = Number(config.leadDays);

        if (frequencyValue <= 0 || leadDays <= 0) {
            return false;
        }

        let totalCalendarDays = 0;
        const maxMonthDays = 31; //Max days by month

        switch (config.timeUnit) {
            case PERIOD_TIME_UNIT_ENUM.DAYS.name:
                totalCalendarDays = frequencyValue;
                break;

            case PERIOD_TIME_UNIT_ENUM.MONTHS.name: {
                totalCalendarDays = frequencyValue * maxMonthDays;
                break;
            }

            case PERIOD_TIME_UNIT_ENUM.YEARS.name: {
                const start = moment(config.startDate || config.start);
                const end = moment(start).add(frequencyValue,  PERIOD_TIME_UNIT_ENUM.YEARS.name.toLowerCase());

                totalCalendarDays = end.diff(start, PERIOD_TIME_UNIT_ENUM.DAYS.name.toLowerCase());
                break;
            }

            default:
                return false;
        }

        return leadDays <= totalCalendarDays;
    };

    /**
     * handle to get data for contractor restriction
     */
    const getData = () => {
        dispatch(userType === USER_TYPE_MODEL.CONTRACTOR_IN_CHARGE ?
            GetContractorRestriction() : GetSupplierRestriction()
        ).then(({ payload: { data: { data, success, code } } }) => {
            if (success) {
                let aux = config.setSettings(data);
                if(aux.renewal ===  FREQUENCY_RENEWAL.CUSTOM_INTERVAL){
                    dispatch(GetPeriodRange(userType)).then(({ payload: { data: { data, success, code } } }) => {
                        if(success){
                            aux = aux.setPeriodRange(data);
                            setConfig(aux);
                            setOriginalInfo(aux);
                        }
                    });
                }else{
                    setConfig(aux);
                    setOriginalInfo(aux);
                }
            } else errorMsg(code);
        });
    }

    /**
     * handle to control change
     * @param evt
     */
    const handleOnChange = (evt) => {
        const value = evt.target.value;
        const name = evt.target.name;
        const currentDay = getCurrentDay();
        if (name === RENEWAL_FIELD_NAME) {
            setConfig((prevState) => (
                {
                    ...prevState,
                    [name]: value,
                    start: currentDay,
                    end: currentDay,
                }
            ));
            if (value === FREQUENCY_RENEWAL.ONE_TIME_ONLY) {
                setConfig((prevState) => (
                    {
                        ...prevState, [name]: value,
                        frequencyValue: 0
                    }
                ));
            }
        } else {
            if(config?.renewal === FREQUENCY_RENEWAL.CUSTOM_INTERVAL &&
                (name === FREQUENCY_VALUE_FIELD_NAME || name === TIME_UNIT_FIELD_NAME || name === START_DATE_FIELD_NAME)){
                const frequencyValue = name === FREQUENCY_VALUE_FIELD_NAME ? value : config?.frequencyValue;
                const timeUnit = name === TIME_UNIT_FIELD_NAME ? value : config?.timeUnit;
                const start = name === START_DATE_FIELD_NAME ? value : config?.startDate;

                setConfig((prevState) => (
                    {
                        ...prevState,
                        start: start,
                        end: calculateRange( start, frequencyValue, timeUnit )
                    }
                ));
            }

            onChange(evt);
        }
    }

    /**
     * handle to update contractor restriction
     */
    const handleUpdate = () => {
        const { renewal, waitTime } = config;
        const valueWaitTime = parseInt(waitTime);

        if (isNaN(valueWaitTime)) {
            setEmptyFields(true);
            return;
        }

        if (renewal === FREQUENCY_RENEWAL.CUSTOM_INTERVAL && (!config.isValidCustom() || !isValidFrequency || !isValidLeadDays || !isValidLeadDaysByMonth)) {
            setEmptyFieldsCustom(true);
            return;
        }

        let frequencyValue = 0;
        let startDatePeriod = getDateLong(config?.start);
        let leadDays = 0;

        if(renewal === FREQUENCY_RENEWAL.CUSTOM_INTERVAL){
            frequencyValue = config.frequencyValue;
            startDatePeriod = getDateLong(config?.startDate);
            leadDays = config.leadDays;
        }

        const body = {
            id: config.id,
            waitTime: config.waitTime,
            renewal: config.renewal,
            frequencyValue: frequencyValue,
            timeUnit: config.timeUnit,
            startDatePeriod: startDatePeriod,
            leadDays: leadDays,
        };

        dispatch(userType === USER_TYPE_MODEL.CONTRACTOR_IN_CHARGE ?
            UpdateContractorRestriction(body) : UpdateSupplierRestriction(body)
        ).then(({ payload: { data: { success, code } } }) => {
            if (success) {
                setModified(true);
                toast.success(t('has_been_updated_successfully'));
            }
            else errorMsg(code)
        });
    }

    return {
        config,
        onChange: (e) => handleOnChange(e),
        TIME_UNIT_OPTIONS_TRANS,
        setConfig,
        handleUpdate,
        emptyFields,
        txtLadDays,
        txtFrequencyValue,
        emptyFieldsCustom,
        isValidFrequency,
        setIsValidFrequency,
        isValidLeadDays,
        setIsValidLeadDays,
        isValidLeadDaysByMonth,
        setIsValidLeadDaysByMonth,
        originalInfo
    };
}

export default ManageContractorsApp;
